import React, { useState, useMemo, useCallback } from "react";
import {
  createStyles,
  Grid,
  makeStyles,
  Typography,
  Box,
} from "@material-ui/core";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import ArrowDropUpIcon from "@material-ui/icons/ArrowDropUp";

import WithLoading from "../../../../utils/WithLoading";
import CardCommon from "../../../../components/card/CardCommon";
import { CustomTheme } from "../../../../types/customTheme";

export interface IntegrationData {
  accountId?: string;
  hubriseAccountId?: string;
  hubriseAccountName?: string;
  accountName?: string;
  [key: string]: any;
}

export interface IntegrationItem {
  id?: string | number;
  type?: string;
  name?: string;
  data?: IntegrationData;
  [key: string]: any;
}

export interface IntegrationsInfoNodeProps {
  isLoading: boolean;
  integrations: IntegrationItem[];
  getAllIntegrationConfig: () => void;
}

const useStyles = makeStyles((theme: CustomTheme) =>
  createStyles({
    headerCard: {
      marginTop: "20px",
      height: "44px",
      display: "flex",
      alignItems: "center",
    },
    headerText: {
      fontWeight: 600,
      fontSize: "0.875rem",
      color: theme.palette.text.primary,
    },
    headerClickable: {
      paddingLeft: "16px",
      display: "flex",
      alignItems: "center",
      cursor: "pointer",
      userSelect: "none",
    },
    centeredItem: {
      display: "flex",
      alignItems: "center",
      height: "100%",
    },
    rightHeaderItem: {
      paddingRight: "16px",
    },
    rowCard: {
      padding: "12px 0",
      marginTop: 6,
      transition: "box-shadow 0.2s ease-in-out",
      "&:hover": {
        boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.08)",
      },
    },
    cellText: {
      fontSize: "0.875rem",
      color: theme.palette.text.secondary,
    },
    leftCell: {
      paddingLeft: "16px",
      fontWeight: 500,
      display: "flex",
    },
    rightCell: {
      paddingRight: "16px",
      display: "flex",
      justifyContent: "flex-end",
    },
    emptyContainer: {
      padding: "32px",
      textAlign: "center",
    },
  }),
);

// Helper function to extract account name dynamically across providers
const getAccountName = (item: IntegrationItem): string => {
  return (
    item.data?.hubriseAccountName ||
    item.data?.accountName ||
    item.data?.accountId ||
    item.name ||
    "-"
  );
};

// Helper function to extract a stable React key
const getAccountKey = (item: IntegrationItem, index: number): string | number => {
  return (
    item.id ||
    item.data?.hubriseAccountId ||
    item.data?.accountId ||
    index
  );
};

const IntegrationsInfoNode: React.FC<IntegrationsInfoNodeProps> = ({
  integrations = [],
  getAllIntegrationConfig
}) => {
  const classes = useStyles();
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const toggleSort = useCallback(() => {
    setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
  }, []);

  // Memoized sorting for render optimization
  const sortedIntegrations = useMemo(() => {
    if (!Array.isArray(integrations)) return [];

    return [...integrations].sort((a, b) => {
      const nameA = getAccountName(a);
      const nameB = getAccountName(b);
      return sortOrder === "asc"
        ? nameA.localeCompare(nameB)
        : nameB.localeCompare(nameA);
    });
  }, [integrations, sortOrder]);

  return (
    <Box width="100%">
      {/* Table Header */}
      <CardCommon
        backgroundColor="table_header_background"
        className={classes.headerCard}
      >
        <Grid container alignItems="center">
          <Grid item xs={6} className={classes.centeredItem}>
            <Typography
              className={`${classes.headerText} ${classes.headerClickable}`}
              align="left"
              onClick={toggleSort}
            >
              Account Name
              {sortOrder === "asc" ? (
                <ArrowDropUpIcon fontSize="small" />
              ) : (
                <ArrowDropDownIcon fontSize="small" />
              )}
            </Typography>
          </Grid>
          <Grid item xs={6} className={`${classes.centeredItem} ${classes.rightHeaderItem}`}>
            <Typography className={classes.headerText} align="right" style={{ width: "100%" }}>
              Type
            </Typography>
          </Grid>
        </Grid>
      </CardCommon>

      {/* Table Body */}
      <Grid container direction="column">
        <Grid item xs={12}>
          {sortedIntegrations.length > 0 ? (
            sortedIntegrations.map((item: IntegrationItem, index: number) => {
              const accountName = getAccountName(item);
              const rowKey = getAccountKey(item, index);

              return (
                <CardCommon
                  key={rowKey}
                  backgroundColor="entity_background"
                  className={classes.rowCard}
                  onClick={() => {item.type === "hubrise" && getAllIntegrationConfig()}}
                >
                  <Grid container alignItems="center">
                    <Grid item xs={6}>
                      <Typography
                        className={`${classes.cellText} ${classes.leftCell}`}
                        align="left"
                      >
                        {accountName}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} className={classes.rightCell}>
                      <Typography className={classes.cellText} align="right">
                        {item.type || "-"}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardCommon>
              );
            })
          ) : (
            <CardCommon
              backgroundColor="entity_background"
              className={classes.emptyContainer}
            >
              <Typography variant="body2" color="textSecondary">
                No integrations found.
              </Typography>
            </CardCommon>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default WithLoading(IntegrationsInfoNode);